import React, { useEffect, useRef, useState } from "react";
import api, { describeError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { VISIBILITY_OPTIONS } from "@/lib/documents";
import { toast } from "sonner";
import { UploadCloud, FileCheck2 } from "lucide-react";

const ACCEPT = ".pdf,.doc,.docx,.jpg,.jpeg,.png";

export default function DocumentUploadDialog({ open, onOpenChange, caseId, clientId, onUploaded }) {
  const [file, setFile] = useState(null);
  const [category, setCategory] = useState("General");
  const [visibility, setVisibility] = useState("firm");
  const [selectedCase, setSelectedCase] = useState(caseId || "");
  const [cases, setCases] = useState([]);
  const [saving, setSaving] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    if (open) {
      setFile(null); setCategory("General"); setVisibility("firm");
      setSelectedCase(caseId || "");
      if (!caseId) api.get("/cases").then(({ data }) => setCases(data)).catch(() => {});
    }
  }, [open, caseId]);

  const submit = async () => {
    if (!file) { toast.error("Please choose a file to upload"); return; }
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("category", category || "General");
      fd.append("visibility", visibility);
      if (selectedCase) fd.append("case_id", selectedCase);
      if (clientId) fd.append("client_id", clientId);
      await api.post("/documents", fd);
      toast.success("Document uploaded");
      onUploaded?.();
      onOpenChange(false);
    } catch (err) {
      toast.error(describeError(err, "Upload failed. Please try again."));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl text-navy">Upload Document</DialogTitle>
          <DialogDescription>PDF, DOC, DOCX, JPG or PNG — up to 15 MB.</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <input ref={inputRef} type="file" accept={ACCEPT} data-testid="doc-file-input" className="hidden"
            onChange={(e) => setFile(e.target.files?.[0] || null)} />
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            data-testid="doc-file-dropzone"
            className="flex w-full flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed border-border bg-secondary/30 py-8 text-center transition-colors hover:border-navy/40"
          >
            {file ? (
              <>
                <FileCheck2 className="h-8 w-8 text-emerald-600" />
                <p className="text-sm font-medium text-navy">{file.name}</p>
                <p className="text-xs text-muted-foreground">Click to choose a different file</p>
              </>
            ) : (
              <>
                <UploadCloud className="h-8 w-8 text-navy/60" />
                <p className="text-sm font-medium text-navy">Click to choose a file</p>
                <p className="text-xs text-muted-foreground">PDF, DOC, DOCX, JPG, PNG</p>
              </>
            )}
          </button>

          {!caseId && (
            <div className="space-y-1.5">
              <Label className="text-xs text-muted-foreground">Related Case (optional)</Label>
              <Select value={selectedCase} onValueChange={setSelectedCase}>
                <SelectTrigger data-testid="doc-case-select"><SelectValue placeholder="No case" /></SelectTrigger>
                <SelectContent>{cases.map((c) => <SelectItem key={c.id} value={c.id}>{c.case_number} — {c.title}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label className="text-xs text-muted-foreground">Category</Label>
              <Input data-testid="doc-category-input" value={category} onChange={(e) => setCategory(e.target.value)} placeholder="Pleadings, Order, Evidence…" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-muted-foreground">Visibility</Label>
              <Select value={visibility} onValueChange={setVisibility}>
                <SelectTrigger data-testid="doc-visibility-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {VISIBILITY_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          {visibility === "client" && (
            <p className="rounded-md bg-emerald-50 px-3 py-2 text-xs text-emerald-700">This document will be visible to the client in their portal.</p>
          )}
        </div>

        <DialogFooter>
          <Button onClick={submit} disabled={saving} data-testid="doc-upload-submit-btn" className="bg-navy text-white hover:bg-navy-light">
            {saving ? "Uploading…" : "Upload"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
